//Import React
import React, { useContext, useState, useEffect } from "react";
import Select, { components } from "react-select";
import { useLocation, useNavigate } from "react-router-dom";
//Import Firebase
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { addDoc, arrayRemove, arrayUnion, collection, doc, Timestamp, updateDoc } from "firebase/firestore";
import { db, storage } from "../../firebase";
//Import Context
import { AppContext } from "../../contexts/AppContext";
import staticData from '../../contexts/staticData.json';
//Import Helpers
import { formatCurrency, formatDate, formatDisplayName, getSubventionNames, preventNonNumericInput } from "../../shared/helpers";
import { safeFolioGeneration } from "../../shared/folioGenerator";
//Import Components
import MessageConfirmation from "../messageConfirmation/MessageConfirmation";
import ViewPurchaseRequestForm from "../Forms/PurchaseForm/ViewPurchaseRequestForm";
//Import Icon
import historyIcon from '../../assets/icons/history.svg';
import documentIcon from '../../assets/icons/document.svg';
import downArrow from '../../assets/icons/downArrow.svg';
import upload from '../../assets/icons/upload.svg';
import walletIcon from '../../assets/icons/walletWhite.svg';
import info from '../../assets/icons/info.svg';
import warning from '../../assets/icons/infoWhite.svg';
//Import CSS
import './viewPaymentRequest.css';
import ViewFeeContractForm from "../Forms/FeeContractForm/ViewFeeContractForm";

function ViewPaymentRequest({ request, establishmentList, pmeList, userData, viewRequest, setView, data, view, viewObserved, viewRejected }) {
    const { requestsList } = useContext(AppContext);
    const [error, setError] = useState(false);
    const [existingPaymentRequest, setExistingPaymentRequest] = useState(null);
    const [remainingBalance, setRemainingBalance] = useState(0);
    const [amountError, setAmountError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showViewPurchaseRequest, setShowViewPurchaseRequest] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentRequest, setPaymentRequest] = useState({
        folio: '',
        paymentType: null,
        amountToBePaid: 0,
        sendTo: null,
        backups: {
            conformityCertificate: null,
            invoice: null,
            deliveryGuide: null,
            feeReceipt: null,
            others: null
        },
        ...(viewObserved && { comments: null })
    })
    const navigate = useNavigate();
    const location = useLocation();
    const pme = pmeList.find(pme => pme.id === request?.idPME)
    const activitySelected = pme.activities.find(act => act.id === request.idActivity);

    useEffect(() => {
            const contenedor = document.querySelector('.view-pending-payment-request'); // o tu clase principal scrollable
            if (contenedor) contenedor.scrollTop = 0;
        }, []);

    useEffect(() => {
        checkExistingPaymentRequest();
    }, [requestsList, request.id]);

    useEffect(() => {
        const generateFolio = () => {
            if (request?.idPME) {
                const newFolio = safeFolioGeneration(request.idPME, requestsList, 'PAY');
                setPaymentRequest(prevState => ({ ...prevState, folio: newFolio }));
            }
        };

        generateFolio();
    }, [request?.idPME, requestsList]);

    const checkExistingPaymentRequest = async () => {
        const existingRequest = requestsList.find(req => req.originRequestId === (request.typeOfRequest === 'payment' ? request.originRequestId : request.id));

        if (existingRequest) {
            const sendToValue = viewRejected ? null : existingRequest.sentTo;
            setExistingPaymentRequest(existingRequest);
            setPaymentRequest(prevState => {
                return {
                    ...prevState,
                    paymentType: {
                        value: existingRequest.paymentType.value,
                        label: existingRequest.paymentType.label
                    },
                    sendTo: sendToValue,
                    amountToBePaid: existingRequest.amountToBePaid
                }
            });
            setRemainingBalance(existingRequest.amountPayable);
        } else {
            setRemainingBalance(request.requestDetails?.purchaseOrderAmount);
        }
    };

    const getEstablishment = () => {
        return establishmentList.find(establishment => establishment.id === request?.idEstablishment);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'comments') {
            setPaymentRequest(prevState => ({
                ...prevState,
                [name]: value
            }));
            return;
        }

        if (name === 'amountToBePaid') {
            // Convertir a número usando parseInt
            const numericValue = parseInt(value.replace(/[^\d]/g, ''), 10);

            // Validar contra remainingBalance usando el valor numérico
            if (numericValue > remainingBalance) {
                setAmountError(true);
                setErrorMessage(`El monto no puede exceder el saldo disponible de ${formatCurrency(remainingBalance)}`);
            } else if (numericValue <= 0 && value !== '') {
                setAmountError(true);
                setErrorMessage('El monto debe ser mayor a 0');
            } else {
                setAmountError(false);
                setErrorMessage('');
            }

            // Guardar el valor numérico (0 si es vacío)
            setPaymentRequest(prevState => ({
                ...prevState,
                [name]: numericValue || 0
            }));
            return;
        }

        setPaymentRequest(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleChangeFiles = async (e) => {
        const { name, files } = e.target;
        if (files.length > 0) {
            const file = files[0];
            setPaymentRequest(prevState => ({
                ...prevState,
                backups: {
                    ...prevState.backups,
                    [name]: file
                }
            }));
        }
    };

    const handleRemoveFile = async (backupType) => {
        setPaymentRequest(prevState => ({
            ...prevState,
            backups: {
                ...prevState.backups,
                [backupType]: null
            }
        }));
    };


    const handleSelectChange = (selectedOption, { name }) => {
        setPaymentRequest(prevState => {
            const newState = {
                ...prevState,
                [name]: selectedOption
            };

            if (name === 'paymentType') {
                if (selectedOption.value === 'fullPayment') {
                    newState.amountToBePaid = request.requestDetails?.purchaseOrderAmount;
                }
            }
            return newState;
        });
    };

    const handleBack = () => {
        setView(false);
    };

    const handleViewFormClick = () => {
        setShowViewPurchaseRequest(true);
    };

    const handleClosePurchaseForm = () => {
        setShowViewPurchaseRequest(false);
    };

    const handleClickHistory = () => {
        const establishment = getEstablishment()?.nameEstablishment;
        const currentState = {
            previousPath: location.pathname,
            selectedRequest: request,
            data: data,
            view: view,
            viewRequest: viewRequest,
            viewObserved: viewObserved,
            viewRejected: viewRejected,
        }
        if (request.typeOfRequest === 'payment') {
            navigate(`/establecimiento/seguimiento/${request.originRequestId}`, {
                state: {
                    establishment,
                    returnState: currentState
                }
            });
        } else {
            navigate(`/establecimiento/seguimiento/${request.id}`, {
                state: {
                    establishment,
                    returnState: currentState
                }
            });
        }

    };

    const handleClickHistoryPayment = () => {
        const establishment = getEstablishment()?.nameEstablishment;

        const currentState = {
            previousPath: location.pathname,
            selectedRequest: request,
            data: data,
            view: view,
            viewRequest: viewRequest,
            viewObserved: viewObserved,
            viewRejected: viewRejected,
        }
        navigate(`/establecimiento/seguimiento-pago/${request.id}`, {
            state: {
                establishment,
                returnState: currentState
            }
        });

    };

    const isFormValid = () => {
        const { paymentType, amountToBePaid, sendTo, backups, comments } = paymentRequest;
        const hasRequiredBackups = backups.conformityCertificate &&
            (backups.deliveryGuide || backups.feeReceipt || backups.invoice);
        if (viewObserved) {
            return sendTo && comments;
        }

        const isAmountValid = !amountError && amountToBePaid > 0;

        return paymentType &&
            isAmountValid &&
            sendTo &&
            hasRequiredBackups;
    };

    const uploadBackups = async (backups) => {
        const uploadPromises = Object.entries(backups)
            .filter(([_, file]) => file !== null)
            .map(async ([backupType, file]) => {
                if (file instanceof File) {
                    const storageRef = ref(storage, `paymentRequests/${backupType}/${file.name}_${Date.now()}`);
                    await uploadBytes(storageRef, file);
                    const downloadURL = await getDownloadURL(storageRef);
                    return [backupType, { name: file.name, url: downloadURL }];
                } else {
                    // Si ya es un objeto con URL, simplemente lo devolvemos
                    return [backupType, file];
                }
            });

        const uploadedFiles = await Promise.all(uploadPromises);
        return Object.fromEntries(uploadedFiles);
    };

    const handleObservedRequest = async () => {
        if (!isFormValid()) {
            return;
        }

        try {
            setIsSubmitting(true);
            const requestRef = doc(db, 'requests', request.id);
            const uploadedBackups = await uploadBackups(paymentRequest.backups);
            const historyArray = request.history;
            const lastHistoryEntry = historyArray[historyArray.length - 1];
            const penultimateHistoryEntry = historyArray[historyArray.length - 2];
            const newResponse = {
                timestamp: Timestamp.now(),
                responsibleName: userData.displayName,
                role: (userData.permissions && userData.permissions === 'admin') ? 'Administrador' : (userData.department || 'Rol desconocido'),
                action: 'Respuesta a observación realizada',
                details: paymentRequest.comments,
                sentTo: penultimateHistoryEntry.sentTo,
                backups: uploadedBackups
            };

            const updatedLastHistoryItem = {
                ...lastHistoryEntry,
                response: [newResponse],
            };

            await updateDoc(requestRef, {
                history: arrayRemove(lastHistoryEntry)
            });
            await updateDoc(requestRef, {
                history: arrayUnion(updatedLastHistoryItem),
                'sentTo': penultimateHistoryEntry.sentTo
            });
            console.log('Se respondio correctamente');
            setIsSubmitting(false);
            setView(true);
        } catch (error) {
            console.error(error);
            setError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCreatePaymentRequest = async () => {
        if (!isFormValid()) {
            return;
        }

        try {
            setIsSubmitting(true);
            const uploadedBackups = await uploadBackups(paymentRequest.backups);
            const historyEntry = {
                timestamp: Timestamp.now(),
                responsibleName: userData.displayName,
                role: (userData.permissions && userData.permissions === 'admin') ? 'Administrador' : userData.department,
                backups: uploadedBackups
            };

            // Si estamos creando desde una solicitud rechazada
            if (request.typeOfRequest === 'payment' && request.state === 'rejected') {
                // Actualizamos la solicitud de pago
                const paymentRequestRef = doc(db, 'requests', request.id);
                await updateDoc(paymentRequestRef, {
                    sentTo: paymentRequest.sendTo?.value,
                    state: 'pending',
                    paymentType: paymentRequest.paymentType,
                    amountToBePaid: paymentRequest.amountToBePaid,
                    amountPayable: paymentRequest.paymentType.value === 'partialPayment'
                        ? remainingBalance - paymentRequest.amountToBePaid
                        : request.purchaseOrderAmount - paymentRequest.amountToBePaid,
                    backups: uploadedBackups, // Actualizamos los backups directamente
                    history: arrayUnion({
                        ...historyEntry,
                        action: 'Solicitud de Pago Reactivada',
                        details: 'Solicitud reactivada después de rechazo',
                        amountToBePaid: paymentRequest.amountToBePaid,
                        sentTo: paymentRequest.sendTo?.value
                    })
                });

                // Actualizamos la solicitud original solo con el campo de paymentRequest
                const originalRequestRef = doc(db, 'requests', request.originRequestId);
                await updateDoc(originalRequestRef, {
                    paymentRequest: (paymentRequest.paymentType && paymentRequest.paymentType.value === 'fullPayment') ? 'done' : 'inProcess'
                });
            }
            else if (existingPaymentRequest) {
                // Actualizar solicitud de pago existente
                const paymentRequestRef = doc(db, 'requests', existingPaymentRequest.id);
                await updateDoc(paymentRequestRef, {
                    sentTo: paymentRequest.sendTo?.value,
                    amountToBePaid: paymentRequest.amountToBePaid,
                    amountPayable: remainingBalance - paymentRequest.amountToBePaid,
                    backups: uploadedBackups, // Actualizamos los backups directamente
                    history: arrayUnion({
                        ...historyEntry,
                        action: 'Solicitud de Pago Actualizada',
                        details: 'Solicitud actualizada para pago adicional',
                        amountToBePaid: paymentRequest.amountToBePaid,
                        sentTo: paymentRequest.sendTo?.value
                    })
                });
            }
            else {
                // Creación de nueva solicitud de pago
                const newPaymentRequest = {
                    folio: paymentRequest.folio,
                    idProject: request.idProject,
                    idEstablishment: request.idEstablishment,
                    idPME: request.idPME,
                    idActivity: request.idActivity,
                    originRequestId: request.id,
                    purchaseOrderAmount: request.requestDetails?.purchaseOrderAmount,
                    sentTo: paymentRequest.sendTo?.value,
                    state: 'pending',
                    typeOfRequest: 'payment',
                    paymentType: paymentRequest.paymentType,
                    amountToBePaid: paymentRequest.amountToBePaid,
                    amountPayable: request.requestDetails?.purchaseOrderAmount - paymentRequest.amountToBePaid,
                    backups: uploadedBackups,
                    created: Timestamp.now(),
                    responsibleName: userData.displayName,
                    responsiblePosition: (userData.permissions && userData.permissions === 'admin') ? 'Administrador' : userData.department,
                    history: [{
                        ...historyEntry,
                        action: 'Solicitud de Pago Creada',
                        details: 'Solicitud creada y enviada para pago',
                        amountToBePaid: paymentRequest.amountToBePaid,
                        sentTo: paymentRequest.sendTo?.value
                    }]
                };

                const requestsCollection = collection(db, 'requests');
                await addDoc(requestsCollection, newPaymentRequest);

                // Actualizamos solo el estado en la solicitud original
                const originalRequestRef = doc(db, 'requests', request.id);
                await updateDoc(originalRequestRef, {
                    paymentRequest: (paymentRequest.paymentType && paymentRequest.paymentType.value === 'fullPayment') ? 'done' : 'inProcess'
                });
            }

            console.log('Solicitud Actualizada Correctamente');
            setIsSubmitting(false);
            setView(true);
        } catch (error) {
            console.error('Error al crear/actualizar solicitud de pago: ', error);
            setError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    //Render Functions
    const renderObservationDetails = () => {
        const lastHistoryItem = request.history && request.history.length > 0
            ? request.history[request.history.length - 1]
            : null;

        if (!lastHistoryItem || lastHistoryItem.responseRequest !== 'observed') return null;

        return (
            <article className='observation-details flex'>
                <p className='text-bold'>Observación recibida</p>
                <article className='observation flex'>
                    <header className='observation-header flex'>
                        <img src={info} className='icon' alt='icono de solicitud observada' />
                        <span>{formatDate(lastHistoryItem.timestamp)} {formatDisplayName(lastHistoryItem.responsibleName)} ({lastHistoryItem.role}) - <strong>{lastHistoryItem.action}</strong></span>
                    </header>
                    <section className='observation-body flex'>
                        <p><strong>Observación: </strong>{lastHistoryItem.details}</p>
                        {lastHistoryItem.backups && lastHistoryItem.backups.length > 0 && (
                            <span className='observation-backups flex'>
                                <p><strong>Archivos Adjuntos:</strong></p>
                                <ul className='backup-list flex'>
                                    {lastHistoryItem.backups.map((backup, index) => (
                                        <li key={index}>
                                            <a href={backup.url} target='_blank' rel='noopener noreferrer'>{backup.name}</a>
                                        </li>
                                    ))}
                                </ul>
                            </span>
                        )}
                    </section>
                </article>
            </article>
        );
    };

    const renderCommentsBox = () => (
        <article className='comments-box flex'>
            <p>Respuesta a la Observación:</p>
            <textarea
                name='comments'
                className='input-comments'
                placeholder='Ingresa tu respuesta a la observación...'
                value={paymentRequest.comments}
                onChange={handleInputChange}>
            </textarea>
        </article>
    );

    return (
        <section className='view-payment-request flex'>
            <header className='header-view-payment flex'>
                <p className='text-bold'>Detalles de Solicitud</p>
                <span className='box-action-header flex'>
                    <button type='button' className='request-history-button flex' onClick={handleClickHistory}>
                        <img src={historyIcon} className='icon' alt='Icono para ver historial de solicitud' />
                        <p>Ver Historial Solicitud</p>
                    </button>
                    {request.typeOfRequest === 'payment' && (
                        <button type='button' className='request-history-button flex' onClick={handleClickHistoryPayment}>
                            <img src={historyIcon} className='icon' alt='Icono para ver historial de solicitud' />
                            <p>Ver Historial Solicitud de Pago</p>
                        </button>
                    )}
                </span>
            </header>
            <article className='info-request flex'>
                <span className='flex'>
                    <p>Fecha Solicitud:</p>
                    <p>{formatDate((request.typeOfRequest === 'payment') ? request.created : request.requestDetails.created)}</p>
                </span>
                <span className='flex'>
                    <p>Establecimiento:</p>
                    <p>{getEstablishment().nameEstablishment}</p>
                </span>
                <span className='flex'>
                    <p>Responsable Solicitud:</p>
                    <p>{formatDisplayName((request.typeOfRequest === 'payment') ? request.responsibleName : request.requestDetails?.responsibleName)}</p>
                </span>
            </article>
            <article className='container-table flex'>
                <table className='table-details-request'>
                    <thead>
                        <tr>
                            <th>Dimensión</th>
                            <th>Nombre Acción</th>
                            <th>Subvención</th>
                            <th>Monto PME</th>
                            <th>Monto O.C / Contrato</th>
                            <th>Gasto Devengado</th>
                            <th>Gasto Efectivo</th>
                            <th>Saldo Devengado</th>
                            <th>Saldo Efectivo</th>
                            <th>Ver Form.</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td data-label='Dimensión'>{formatDisplayName(activitySelected?.dimension)}</td>
                            <td data-label='Nombre Acción'>{formatDisplayName(activitySelected?.nameOfAction)}</td>
                            <td data-label='Subvención'>{getSubventionNames(activitySelected?.subsidies)}</td>
                            <td data-label='Monto PME'>{formatCurrency(activitySelected?.totalSubsidyAmount)}</td>
                            <td data-label='Monto O.C / Contrato'>{formatCurrency((request.typeOfRequest === 'payment') ? request.purchaseOrderAmount : request.requestDetails?.purchaseOrderAmount)}</td>
                            <td data-label='Gasto Devengado'>{formatCurrency(activitySelected?.accruedExpense)}</td>
                            <td data-label='Gasto Efectivo'>{formatCurrency(activitySelected?.accruedBalance)}</td>
                            <td data-label='Saldo Devengado'>{formatCurrency(activitySelected?.effectiveExpense)}</td>
                            <td data-label='Saldo Efectivo'>{formatCurrency(activitySelected?.effectiveBalance)}</td>
                            <td data-label='Ver Formulario'>
                                <img
                                    src={documentIcon}
                                    className='icon'
                                    alt='Icono para ver formulario'
                                    onClick={handleViewFormClick}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </article>
            <article className='payment-details flex'>
                <p className='text-bold'>Detalles de Pago</p>
                <span className='payment-details-box flex'>
                    {!viewObserved && (
                        <span className='box middle border flex'>
                            <p>Enviar a (*)</p>
                            <span className='select flex'>
                                <label className='label-hidden' htmlFor='sendTo'>Enviar a</label>
                                <Select
                                    name='sendTo'
                                    id='sendTo'
                                    options={staticData.departments.filter(department => department !== 'Establecimiento').map((department) => ({
                                        value: department,
                                        label: department
                                    }))}
                                    placeholder='Selecciona departamento'
                                    className='custom-select'
                                    classNamePrefix='custom-select'
                                    components={{ DropdownIndicator }}
                                    onChange={handleSelectChange}
                                    value={paymentRequest.sendTo}
                                />
                            </span>
                        </span>
                    )}

                    <span className='box flex'>
                        <p>Monto Orden de Compra / Contrato</p>
                        <p>{formatCurrency((request.typeOfRequest === 'payment') ? request.purchaseOrderAmount : request.requestDetails?.purchaseOrderAmount)}</p>
                    </span>
                    <span className='box middle flex'>
                        <p>Tipo de Pago (*)</p>
                        <span className='select flex'>
                            <label className='label-hidden' htmlFor='paymentType'>Tipo de Pago</label>
                            <Select
                                name='paymentType'
                                id='paymentType'
                                options={staticData.paymentType}
                                placeholder='Selecciona Tipo de Pago'
                                className='custom-select'
                                classNamePrefix='custom-select'
                                components={{ DropdownIndicator }}
                                onChange={handleSelectChange}
                                value={paymentRequest.paymentType}
                                isDisabled={existingPaymentRequest}
                            />
                        </span>
                    </span>
                    {existingPaymentRequest && (
                        <span className='box flex'>
                            <p>Saldo Pendiente a Pagar</p>
                            <p>{formatCurrency(remainingBalance)}</p>
                        </span>
                    )}
                    <span className='box flex'>
                        <p>Monto a Pagar (*)</p>
                        <span className='input flex'>
                            <label htmlFor='amountToBePaid' className='label-hidden'>Monto a Pagar</label>
                            <input
                                type='text'
                                name='amountToBePaid'
                                id='amountToBePaid'
                                className='input-text'
                                placeholder='Ingresa Monto a Pagar'
                                onChange={handleInputChange}
                                value={formatCurrency(paymentRequest.amountToBePaid)}
                                disabled={paymentRequest.paymentType?.value === 'fullPayment' || viewObserved}
                            />
                        </span>
                    </span>
                </span>
                {amountError && errorMessage && (
                    <span className='error-message'>{errorMessage}</span>
                )}
            </article>
            {renderObservationDetails()}
            {viewObserved && renderCommentsBox()}
            <article className='backups flex'>
                <p className='text-bold'>Adjuntar Archivos de Respaldo</p>
                <article className='backups-boxes'>
                    {['conformityCertificate', 'invoice', 'deliveryGuide', 'feeReceipt', 'others'].map((backupType) => (
                        <aside key={backupType} className='box flex'>
                            <p>{staticData.backupTypes2[backupType]}</p>
                            <span className='input flex'>
                                {paymentRequest.backups[backupType] ? (
                                    <span className='li flex'>
                                        <p>{paymentRequest.backups[backupType].name}</p>
                                        <button onClick={() => handleRemoveFile(backupType)} className='delete-button'>X</button>
                                    </span>
                                ) : (
                                    <>
                                        <label className='button-add flex' htmlFor={backupType}>
                                            <img src={upload} className='icon' alt='Icono para subir respaldo.' />
                                            <p>Subir</p>
                                        </label>
                                        <input
                                            id={backupType}
                                            name={backupType}
                                            type='file'
                                            style={{ display: 'none' }}
                                            onChange={handleChangeFiles}
                                        />
                                    </>
                                )}
                            </span>
                        </aside>
                    ))}
                </article>
                <article className={`box-actions flex ${(!isFormValid() || isSubmitting) ? 'disabled' : ''}`}>
                    <button className='button-box flex' disabled={!isFormValid() || isSubmitting} onClick={viewObserved ? handleObservedRequest : handleCreatePaymentRequest}>
                        <img src={walletIcon} className='icon' alt='Icono de Billetera para crear solicitud de pago' />
                        <p>{viewObserved ? 'Responder Observación' : (isSubmitting ? 'Enviando Solicitud...' : 'Crear Solicitud de Pago')}</p>
                    </button>
                    <button className='back-button' onClick={handleBack}>Volver</button>
                    <span className='tooltip-message flex'>
                        <img src={warning} alt='Icono de adevertencia botón deshabilitado' />
                        <p>Existen campos obligatorios incompletos.</p>
                    </span>
                </article>
            </article>
            {showViewPurchaseRequest && (
                request?.typeOfRequest === 'feeContract' ? (
                    <ViewFeeContractForm
                        onClose={handleClosePurchaseForm}
                        requestDetails={{ request: request, activity: activitySelected }}
                        establishmentList={establishmentList}
                        fromViewPayment={true}
                    />
                ) : (
                    <ViewPurchaseRequestForm
                        onClose={handleClosePurchaseForm}
                        requestDetails={{ request: request, activity: activitySelected }}
                        establishmentList={establishmentList}
                        fromViewPayment={true}
                    />
                )

            )}
            <MessageConfirmation error={error} view={true} />
        </section>
    );
};

const DropdownIndicator = (props) => {
    return (
        <components.DropdownIndicator {...props}>
            <img src={downArrow} alt="CustomArrow" />
        </components.DropdownIndicator>
    );
};

export default ViewPaymentRequest;